import React, { useState, useEffect, FC, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';

/**
 * This component handles password reset functionality.
 * Based on solutions from https://github.com/supabase-community/auth-ui/issues/77
 * 
 * The key approach is to directly update the password using the token_hash from
 * either the code parameter or the hash parameters, avoiding session issues.
 */
// Super simple version with maximum logging
const UpdatePassword: FC = () => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('[LOG] Component mounted', {
      time: new Date().toISOString(),
      url: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
    });
  }
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{success: boolean; message: string} | null>(null);
  const [isRecoveryFlowActive, setIsRecoveryFlowActive] = useState(false);
  const { language } = useLanguage();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const messageRef = useRef<HTMLDivElement>(null);

  // Listen for Supabase auth state changes, specifically PASSWORD_RECOVERY
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[LOG] Setting up onAuthStateChange listener', {
        url: window.location.href,
        hash: window.location.hash,
        search: window.location.search,
        time: new Date().toISOString(),
      });
    }

    // Detect both ?code=... and #type=recovery as recovery triggers
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const isRecoveryByCode = !!code;
    const isRecoveryByHash = window.location.hash.includes('type=recovery');
    if (isRecoveryByCode || isRecoveryByHash) {
      setIsRecoveryFlowActive(true);
      if (process.env.NODE_ENV !== 'production') {
        console.log('[LOG] Recovery flow detected', {
          detectedBy: isRecoveryByCode ? 'query-param-code' : 'hash-type-recovery',
          code,
          hash: window.location.hash,
          url: window.location.href,
          time: new Date().toISOString(),
        });
      }
    }

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[LOG] onAuthStateChange event', {
          event,
          sessionPresent: !!session,
          url: window.location.href,
          hash: window.location.hash,
          search: window.location.search,
          time: new Date().toISOString(),
        });
      }
      if (event === 'PASSWORD_RECOVERY') {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[LOG] PASSWORD_RECOVERY event detected', {
            session,
            url: window.location.href,
            hash: window.location.hash,
            search: window.location.search,
            time: new Date().toISOString(),
          });
        }
        setIsRecoveryFlowActive(true);
        // Supabase client handles the token from the URL fragment internally.
        // Clean the URL fragment now that it has been processed by Supabase.
        if (window.location.hash) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('[LOG] Clearing URL hash after PASSWORD_RECOVERY', {
              urlBefore: window.location.href,
              hash: window.location.hash,
              time: new Date().toISOString(),
            });
          }
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          if (process.env.NODE_ENV !== 'production') {
            console.log('[LOG] URL after clearing hash', {
              urlAfter: window.location.href,
              hash: window.location.hash,
              time: new Date().toISOString(),
            });
          }
        }
      } else if (event === 'USER_UPDATED') {
        // Password (or other user data) has just been updated
        if (process.env.NODE_ENV !== 'production') {
          console.log('[LOG] USER_UPDATED event detected – treating as successful password change', {
            sessionPresent: !!session,
            url: window.location.href,
            time: new Date().toISOString(),
          });
        }
        // Make sure the UI reflects success even if updateUser didn’t resolve yet
        setIsSubmitting(false);
        setResult({
          success: true,
          message: 'Password updated successfully! Redirecting to login...'
        });
        setPassword('');
        setConfirmPassword('');
        // Small delay so the user can read the message
        setTimeout(() => {
          navigate(`/${language}/sign-in`);
        }, 2000);
      } else if (event === 'SIGNED_IN' && session?.user) {
        // Check if this SIGNED_IN event is related to recovery
        // A common pattern is SIGNED_IN then PASSWORD_RECOVERY
        if (process.env.NODE_ENV !== 'production') {
          console.log('[LOG] SIGNED_IN event detected', {
            user: session.user,
            url: window.location.href,
            hash: window.location.hash,
            search: window.location.search,
            time: new Date().toISOString(),
          });
        }
        // If a hash with recovery type was present, this SIGNED_IN is part of it.
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        if (hashParams.get('type') === 'recovery') {
          if (process.env.NODE_ENV !== 'production') {
            console.log('[LOG] SIGNED_IN event seems to be part of password recovery flow (hash detected)', {
              hash: window.location.hash,
              url: window.location.href,
              time: new Date().toISOString(),
            });
          }
          // Waiting for PASSWORD_RECOVERY event to set isRecoveryFlowActive
        } else {
          if (process.env.NODE_ENV !== 'production') {
            console.log('[LOG] SIGNED_IN event not directly identified as recovery by hash', {
              hash: window.location.hash,
              url: window.location.href,
              time: new Date().toISOString(),
            });
          }
        }
      } else if (event === 'INITIAL_SESSION') {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[LOG] INITIAL_SESSION event', {
            session,
            url: window.location.href,
            hash: window.location.hash,
            search: window.location.search,
            time: new Date().toISOString(),
          });
        }
        // This might fire if the user is already logged in and visits the page.
        // Or if Supabase client initializes with a session from the recovery link immediately.
        // Check if hash contains recovery info, onAuthStateChange might fire PASSWORD_RECOVERY next.
        if (window.location.hash.includes('type=recovery')) {
            console.log('[RESET] INITIAL_SESSION with recovery hash. PASSWORD_RECOVERY event should follow.');
        }
      }
    });

    // Fallback detection block removed: initial URL and hash/code detection handles recovery flow.

    return () => {
      console.log('[RESET] Unsubscribing from onAuthStateChange listener.');
      if (authListener && authListener.subscription && authListener.subscription.unsubscribe) {
         authListener.subscription.unsubscribe();
       } else {
        console.warn('[RESET] authListener or unsubscribe method not available.');
      }
    };
  }, []); // Empty dependency array to run once on mount and clean up on unmount

  // scroll to message when result changes
  useEffect(() => {
    if (result && messageRef.current) {
      messageRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [result]);

  // warn on navigation during submit
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    if (isSubmitting) window.addEventListener('beforeunload', handleBeforeUnload);
    else window.removeEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isSubmitting]);

  const handleUpdatePassword = async (e: React.FormEvent) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[LOG] handleUpdatePassword called', {
        url: window.location.href,
        hash: window.location.hash,
        search: window.location.search,
        time: new Date().toISOString(),
        passwordLength: password.length,
        confirmPasswordLength: confirmPassword.length,
      });
    }
    e.preventDefault();
    console.log('[RESET] Form submitted');
    
    if (password !== confirmPassword) {
      console.warn('[RESET] Passwords do not match');
      setResult({ success: false, message: 'Passwords do not match.' });
      return;
    }

    if (!password || password.length < 6) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[LOG] Password too short on submit', {
          passwordLength: password.length,
          url: window.location.href,
          time: new Date().toISOString(),
        });
      }
      setResult({ success: false, message: t('password_too_short') });
      return;
    }

    if (!isRecoveryFlowActive) {
      console.error('[RESET] Password recovery flow not active. User might need to use the link from email again.');
      setResult({
        success: false,
        message: 'Password recovery session not active or expired. Please use the password reset link from your email. If you just clicked the link, please wait a moment for the session to activate.'
      });
      return;
    }
    
    setIsSubmitting(true);
    setResult(null);
    if (process.env.NODE_ENV !== 'production') {
      console.log('[LOG] Submitting password update', {
        url: window.location.href,
        hash: window.location.hash,
        search: window.location.search,
        time: new Date().toISOString(),
      });
    }
    
    // With onAuthStateChange handling PASSWORD_RECOVERY, Supabase client has the session.
    // Simply call updateUser with the new password.
    try {
      console.log('[RESET] Attempting password update via supabase.auth.updateUser()');
      const { data, error } = await supabase.auth.updateUser({ password });

      if (error) {
        console.error('[RESET] supabase.auth.updateUser() failed:', error);
        if (process.env.NODE_ENV !== 'production') {
          console.error('[LOG] Error updating password', {
            error,
            url: window.location.href,
            time: new Date().toISOString(),
          });
        }
        setResult({
          success: false,
          message: `Failed to update password: ${error.message}`
        });
      } else {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[LOG] Password updated successfully', {
            url: window.location.href,
            time: new Date().toISOString(),
            nextUrl: `/${language}/sign-in`,
          });
        }
        setResult({
          success: true,
          message: 'Password updated successfully! Redirecting to your profile...'
        });
        setPassword('');
        setConfirmPassword(''); // Clear confirm password field as well
        setTimeout(() => {
          if (process.env.NODE_ENV !== 'production') {
            console.log('[LOG] Navigating to profile after password update', {
              targetUrl: `/${language}/profile`,
              time: new Date().toISOString(),
            });
          }
          navigate(`/${language}/profile`);
        }, 2000);
      }
    } catch (error: any) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[LOG] Password update failed', {
          error,
          url: window.location.href,
          time: new Date().toISOString(),
        });
      }
      setResult({
        success: false,
        message: `An unexpected error occurred: ${error.message || 'Unknown error'}`
      });
    } finally {
      setIsSubmitting(false);
      if (process.env.NODE_ENV !== 'production') {
        console.log('[LOG] handleUpdatePassword finished', {
          url: window.location.href,
          time: new Date().toISOString(),
        });
      }
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex flex-col">
      <div className="flex-grow flex items-center justify-center px-4">
        <div className="max-w-md w-full space-y-8 p-8 rounded-2xl border border-[var(--border-secondary)] bg-[var(--bg-secondary)] shadow-2xl">
          <h2 className="text-center text-3xl font-bold text-[var(--text-primary)]">
            Update Password
          </h2>

          {result && (
            <div ref={messageRef} role="status" aria-live="polite" className={`p-4 rounded ${result.success ? 'bg-[var(--success)]/20 text-[var(--success)]' : 'bg-[var(--error)]/20 text-[var(--error)]'}`}>
              {result.message}
            </div>
          )}

          <form onSubmit={handleUpdatePassword} className="space-y-6">
            {/* hidden email for password managers */}
            <input type="email" name="email" autoComplete="email" hidden />
            <fieldset>
              <legend className="sr-only">Reset Password</legend>
              <div>
                <label htmlFor="password" className="text-[var(--text-primary)]">
                  New Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-[var(--bg-tertiary)] border-[var(--border-primary)] text-[var(--text-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  disabled={isSubmitting || result?.success}
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className="text-[var(--text-primary)]">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-[var(--bg-tertiary)] border-[var(--border-primary)] text-[var(--text-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  disabled={isSubmitting || result?.success}
                />
              </div>
            </fieldset>

            <button
              type="submit"
              disabled={isSubmitting || password.length < 6 || confirmPassword !== password || result?.success}
              className="w-full py-2 px-4 bg-[var(--brand-primary)] text-[var(--text-button)] rounded-md hover:bg-[var(--brand-primary)]/90 disabled:opacity-50 disabled:cursor-not-allowed shadow"
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin border-2 border-t-transparent border-[var(--text-button)] rounded-full w-4 h-4 inline-block mr-2"></span>
                  Updating...
                </>
              ) : 'Update Password'}
            </button>
            
            {process.env.NODE_ENV !== 'production' && !isRecoveryFlowActive && !result && (
              <p className="text-[var(--warning)] text-sm text-center">
                Debug: Password recovery flow not active. Waiting for PASSWORD_RECOVERY event.
              </p>
            )}

            {process.env.NODE_ENV !== 'production' && (
              <div className="text-xs text-[var(--text-tertiary)] pt-4">
                Debug info: Recovery Flow Active? {isRecoveryFlowActive ? 'Yes' : 'No'}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdatePassword;